import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const FILTERS = [
  { id: "pending", label: "Pendientes" },
  { id: "approved", label: "Aprobadas" },
  { id: "rejected", label: "Rechazadas" },
  { id: "", label: "Todas" },
];

const STATUS_BADGE = {
  pending: { bg: "#FFC53D", color: "#1C1917", label: "PENDIENTE" },
  approved: { bg: "#46A758", color: "#FFFFFF", label: "APROBADA" },
  rejected: { bg: "#E5484D", color: "#FFFFFF", label: "RECHAZADA" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminPayments({ embedded = false }) {
  const { user, loading } = useAuth();
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  async function load() {
    try {
      const url = filter ? `/admin/payment-requests?status=${filter}` : "/admin/payment-requests";
      const { data } = await api.get(url);
      setItems(data.requests || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function approve(rid) {
    setBusy(rid);
    try {
      await api.post(`/admin/payment-requests/${rid}/approve`);
      toast.success("Suscripción activada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  }
  async function reject(rid) {
    if (!window.confirm("¿Rechazar esta solicitud?")) return;
    setBusy(rid);
    try {
      await api.post(`/admin/payment-requests/${rid}/reject`);
      toast.success("Solicitud rechazada");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  }

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const inner = (
    <>
      {!embedded && (
        <>
          <span className="eyebrow text-xs">Panel administrador</span>
          <h1 className="hero-display text-4xl sm:text-5xl text-[#1C1917] mt-2">Solicitudes de pago</h1>
        </>
      )}
      <p className="text-sm text-[#57534E] mt-2 max-w-2xl">
        Revisa cada transacción contra tu cuenta de PayPal antes de aprobar. Una vez aprobada, la suscripción del usuario se activa automáticamente con el plan correspondiente.
      </p>

      <div className="mt-6 inline-flex border-2 border-[#1C1917] shadow-[3px_3px_0_0_#1C1917] flex-wrap" data-testid="admin-filters">
        {FILTERS.map((f, i) => (
          <button
            key={f.id || "all"}
            onClick={() => setFilter(f.id)}
            className={`px-5 h-11 font-black text-xs tracking-wider uppercase transition-colors ${i > 0 ? "border-l-2 border-[#1C1917]" : ""} ${
              filter === f.id ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
            }`}
            data-testid={`filter-${f.id || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div data-testid="admin-payments-embedded">
        {inner}
        <div className="mt-6 space-y-4">
          {renderItems(items, busy, approve, reject)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7]" data-testid="admin-payments-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
        {inner}
        <div className="mt-6 space-y-4">
          {renderItems(items, busy, approve, reject)}
        </div>
      </div>
    </div>
  );
}

function renderItems(items, busy, approve, reject) {
  if (items.length === 0) {
    return (
      <div className="border-2 border-[#1C1917] bg-white p-10 text-center text-[#57534E] shadow-[3px_3px_0_0_#1C1917]">
        No hay solicitudes en este filtro.
      </div>
    );
  }
  return items.map((r) => {
    const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
    return (
      <div key={r.id} className="border-2 border-[#1C1917] bg-white p-6 shadow-[3px_3px_0_0_#1C1917]" data-testid={`req-row-${r.id}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="inline-block px-3 py-1 text-[10px] font-black tracking-widest uppercase border-2 border-[#1C1917]"
                style={{ background: s.bg, color: s.color }}
              >
                {s.label}
              </span>
              <span className="text-xs font-bold uppercase tracking-wider text-[#1C1917]">
                {r.plan_id === "annual" ? "PLAN ANUAL · 49,99 €" : "PLAN MENSUAL · 6,99 €"}
              </span>
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div>
                <span className="text-[#57534E] uppercase text-[10px] font-bold tracking-wider">Usuario</span>
                <div className="text-[#1C1917]">
                  <strong>{r.user_name || "—"}</strong>
                  <span className="text-[#57534E]"> · {r.user_email}</span>
                </div>
              </div>
              <div>
                <span className="text-[#57534E] uppercase text-[10px] font-bold tracking-wider">PayPal Txn ID</span>
                <div className="font-mono text-xs text-[#1C1917] break-all">{r.paypal_txn_id}</div>
              </div>
              <div>
                <span className="text-[#57534E] uppercase text-[10px] font-bold tracking-wider">Enviado</span>
                <div className="text-[#1C1917]">{formatDate(r.created_at)}</div>
              </div>
              {r.reviewed_at && (
                <div>
                  <span className="text-[#57534E] uppercase text-[10px] font-bold tracking-wider">Revisado</span>
                  <div className="text-[#1C1917]">{formatDate(r.reviewed_at)}</div>
                </div>
              )}
            </div>
            {r.note && (
              <div className="mt-3 text-xs text-[#57534E] border-l-4 border-[#FFC53D] pl-3 italic">
                "{r.note}"
              </div>
            )}
          </div>
          {r.status === "pending" && (
            <div className="flex flex-col gap-2 min-w-[140px]">
              <Button
                onClick={() => approve(r.id)}
                disabled={busy === r.id}
                className="rounded-none h-10 border-2 border-[#1C1917] bg-[#46A758] text-white hover:bg-[#3D9650] font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917]"
                data-testid={`approve-${r.id}`}
              >
                ✓ Aprobar
              </Button>
              <Button
                onClick={() => reject(r.id)}
                disabled={busy === r.id}
                variant="ghost"
                className="rounded-none h-10 border-2 border-[#1C1917] bg-white text-[#E5484D] hover:bg-[#E5484D] hover:text-white font-black text-xs tracking-widest uppercase"
                data-testid={`reject-${r.id}`}
              >
                ✗ Rechazar
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  });
}
